/**
 * Marketing-attribution capture (UTM + referrer).
 *
 * First-touch attribution for the signup funnel. We read the standard UTM
 * params (utm_source / utm_medium / utm_campaign) from the URL query the
 * moment a visitor lands, persist them to sessionStorage so they survive
 * in-app navigation and form re-renders, then forward them in the signup
 * request BODY.
 *
 * Why sessionStorage and not localStorage: a visitor's UTM tags belong to
 * THIS visit. localStorage would leak a stale campaign onto a later,
 * unrelated signup from the same device. sessionStorage clears when the tab
 * closes, which matches "first touch of this session".
 *
 * Why the body and not a cookie: the marketing host and the app host may be
 * different origins (e.g. pulsesignal.co vs app.pulsesignal.co), and cookies
 * do not cross origins. The backend reads the body first and only falls back
 * to cookies for the same-origin case, so the body is the reliable channel.
 *
 * All functions are SSR-safe: they no-op (return empty) when window is
 * undefined, so they can be imported into client components without breaking
 * the server render.
 */

export interface MarketingParams {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  signup_referrer?: string
}

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign'] as const
type UtmKey = (typeof UTM_KEYS)[number]

const STORAGE_PREFIX = 'ps_'
// sessionStorage keys: ps_utm_source, ps_utm_medium, ps_utm_campaign,
// ps_signup_referrer.
const REFERRER_KEY = `${STORAGE_PREFIX}signup_referrer`

// Cap each stored value so a hostile or malformed URL cannot bloat the
// request body. Backend columns are 255 chars; mirror that here.
const MAX_LEN = 255

function clean(value: string | null | undefined): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim().slice(0, MAX_LEN)
  return trimmed.length > 0 ? trimmed : undefined
}

function isBrowser(): boolean {
  return typeof window !== 'undefined'
}

/** Read UTM params from the current URL query string. */
export function getUtmParamsFromUrl(): MarketingParams {
  if (!isBrowser()) return {}
  let search: string
  try {
    search = window.location.search
  } catch {
    return {}
  }
  const params = new URLSearchParams(search)
  const out: MarketingParams = {}
  for (const key of UTM_KEYS) {
    const v = clean(params.get(key))
    if (v) out[key] = v
  }
  return out
}

/** Persist any present UTM values + the referrer to sessionStorage. */
export function storeMarketingParams(): void {
  if (!isBrowser()) return
  let store: Storage
  try {
    store = window.sessionStorage
  } catch {
    // Private-mode / disabled storage. Capture is best-effort, never throws.
    return
  }
  try {
    const fromUrl = getUtmParamsFromUrl()
    for (const key of UTM_KEYS) {
      const v = fromUrl[key]
      // First touch wins: only write if the slot is empty AND the URL
      // carries a value, so a later in-session navigation without UTM tags
      // does not clobber the original campaign.
      if (v && !store.getItem(`${STORAGE_PREFIX}${key}`)) {
        store.setItem(`${STORAGE_PREFIX}${key}`, v)
      }
    }
    if (!store.getItem(REFERRER_KEY)) {
      const ref = clean(document.referrer)
      if (ref) store.setItem(REFERRER_KEY, ref)
    }
  } catch {
    // No-op: never let attribution capture disrupt the page.
  }
}

function readStored(key: UtmKey): string | undefined {
  if (!isBrowser()) return undefined
  try {
    return clean(window.sessionStorage.getItem(`${STORAGE_PREFIX}${key}`))
  } catch {
    return undefined
  }
}

function readStoredReferrer(): string | undefined {
  if (!isBrowser()) return undefined
  try {
    return clean(window.sessionStorage.getItem(REFERRER_KEY))
  } catch {
    return undefined
  }
}

/**
 * Resolve the marketing params to forward with a signup, preferring the URL
 * (always fresh) then sessionStorage (persisted from the first touch). The
 * referrer falls back to the live document.referrer when nothing was stored.
 */
export function getMarketingParams(): MarketingParams {
  const fromUrl = getUtmParamsFromUrl()
  const out: MarketingParams = {}
  for (const key of UTM_KEYS) {
    const v = fromUrl[key] ?? readStored(key)
    if (v) out[key] = v
  }
  const ref =
    readStoredReferrer() ??
    (isBrowser() ? clean(document.referrer) : undefined)
  if (ref) out.signup_referrer = ref
  return out
}
